import { useField } from "formik";
import { type InputHTMLAttributes, useId } from "react";

import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import { cn } from "@src/shared/lib/utils";

/**
 * Formats the given value into a (maybe partially entered) zip code.
 * Mirrors the legacy `<ZipCodeInputFormik />` formatter from `@el8/vital-formik`.
 *
 * - Strips out any non-digit characters.
 * - For 6+ digits, inserts a `-` between the first 5 and the rest (ZIP+4).
 */
export const formatZipCode = (value: string): string => {
  const digits = value.replace(/[^\d]/g, "");
  if (digits.length > 5) {
    return `${digits.substring(0, 5)}-${digits.substring(5, 9)}`;
  }
  return digits;
};

const ZIP_REGEX = /^[\d-]*$/;

export interface FormikZipInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "name" | "value" | "onChange" | "onBlur" | "type"
  > {
  name: string;
  label?: string;
  hint?: string;
  required?: boolean;
  containerClassName?: string;
}

/**
 * Formik-integrated ZIP code input. Auto-formats values as the user types,
 * inserting a `-` between the 5-digit prefix and the optional 4-digit suffix.
 */
export const FormikZipInput = ({
  name,
  label,
  hint,
  required,
  containerClassName,
  className,
  id,
  maxLength = 10,
  inputMode = "numeric",
  autoComplete = "postal-code",
  ...props
}: FormikZipInputProps) => {
  const [field, meta, helpers] = useField<string>(name);
  const reactId = useId();
  const inputId = id ?? reactId;
  const error = meta.touched && meta.error ? String(meta.error) : undefined;

  return (
    <VerticalFlexbox gap="xs" className={cn("w-full", containerClassName)}>
      {label ? (
        <Label htmlFor={inputId} className="text-sm text-foreground">
          {label}
          {required ? (
            <span className="ml-0.5 text-destructive" aria-hidden>
              *
            </span>
          ) : null}
        </Label>
      ) : null}
      <Input
        {...props}
        id={inputId}
        name={field.name}
        value={field.value ?? ""}
        onChange={(event) => {
          const next = event.target.value;
          if (!ZIP_REGEX.test(next)) {
            return;
          }
          helpers.setValue(formatZipCode(next));
        }}
        onBlur={field.onBlur}
        required={required}
        maxLength={maxLength}
        inputMode={inputMode}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : undefined}
        className={cn(
          error && "border-destructive ring-destructive",
          className,
        )}
      />
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </VerticalFlexbox>
  );
};
